/**
 * LifeAtUniBento — Feb 2026 v2: reduced from a 6-tile masonry to a
 * single wide HERO image, based on user feedback that the strip was
 * visually noisy above the "Will you enjoy it?" cluster.
 *
 * Image precedence for the hero pick (unchanged from v1, just uses
 * the first winner instead of six):
 *   1. First real `universities.gallery` entry (Google Places seed,
 *      highest-rated photo first per Places API convention).
 *   2. Any explicit `fallbackPhotos` prop passed in.
 *   3. Deterministic per-university pick from the shared photo pool.
 *
 * Renders nothing when no image resolves — keeps the pageless page
 * genuinely image-free rather than showing a broken tile.
 */
import { pickPhotosForUni } from "@/lib/lifeAtUniPhotoPool";

export default function LifeAtUniBento({
  universityName,
  gallery = [],
  fallbackPhotos = [],
  degreeId,
  universityId,
}) {
  // `universities.gallery` items may be `{url, attribution}` objects
  // (Google Places backfill) or raw URL strings from older callers.
  const toUrl = (item) => {
    if (!item) return null;
    if (typeof item === "string") return item;
    if (typeof item === "object") return item.url || item.src || item.href || null;
    return null;
  };
  const explicit = [...(gallery || []), ...(fallbackPhotos || [])]
    .map(toUrl)
    .filter(Boolean);
  // Pick the single best available hero URL.
  const hero =
    explicit[0] ||
    pickPhotosForUni(universityId || universityName || "unknown-uni", 1)[0] ||
    null;
  if (!hero) return null;

  const usingFallback = explicit.length === 0;

  return (
    <section data-testid="life-at-uni-gallery" className="mt-12">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-editorial text-2xl sm:text-3xl text-stone-900">
          Life at {universityName}
        </h2>
        {usingFallback && (
          <span className="text-[11px] font-underground-sc tracking-wider text-stone-500">
            Campus & student life
          </span>
        )}
      </div>
      <div
        data-testid="life-photo-hero"
        className="aspect-[16/9] relative overflow-hidden rounded-2xl border border-stone-200 bg-stone-100"
      >
        <img
          src={hero}
          alt={universityName ? `Campus and student life at ${universityName}` : "Campus and student life"}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover hover:scale-[1.02] transition-transform duration-700"
        />
      </div>
    </section>
  );
}
